import { useState, useEffect, useRef } from 'react';
import { Pencil } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface EditableInputProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  fontSize?: string;
  className?: string;
  disabled?: boolean;
}

export const EditableInput = ({
  value,
  onSave,
  placeholder = '',
  fontSize = 'text-lg md:text-xl lg:text-3xl',
  className,
  disabled = false,
}: EditableInputProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (inputValue.trim() === value || inputValue.trim() === '') {
      setIsEditing(false);
      setInputValue(value);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(inputValue.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      setInputValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setInputValue(value);
      setIsEditing(false);
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving || disabled}
        placeholder={placeholder}
        className={cn('h-9 md:h-11 w-full', fontSize, className)}
      />
    );
  }

  return (
    <button
      onClick={() => !disabled && setIsEditing(true)}
      disabled={disabled}
      className={cn(
        'group hover:bg-muted/50 px-2 md:px-4 py-1.5 md:py-2 rounded-md transition-colors min-w-0 flex items-center gap-2',
        className
      )}
    >
      <span className={cn('truncate', fontSize)}>
        {value || placeholder}
      </span>
      <Pencil className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
};
